import chatBubbleModule from '@/store/modules/chat-bubble.module'
import stateTreeUtils from '@/utils/state-tree.utils'
import { ACTION_TYPES, ANSWER_END, EXPRESSION_TYPES, INPUT_TYPES, STATE_ID } from '@/utils/bot-json.constants'
import { checkIsObject, checkIsString } from '@/utils/preconditions'
import { EDGE_TRANSITION_TYPES, NODE_TYPES } from '@/utils/state-tree.generator'
import find from 'ramda/src/find'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import clone from 'ramda/src/clone'
import last from 'ramda/src/last'
import propEq from 'ramda/src/propEq'
import propSatisfies from 'ramda/src/propSatisfies'
import move from 'ramda/src/move'
import isNil from 'ramda/src/isNil'
import PreviewController from './preview.controller'
import conversationModule from '@/store/modules/conversation.module'
import pathEq from 'ramda/src/pathEq'
import { CONVERSATION_TYPES, SECTION_FOCUSES } from '@/store/constants'
import toaster from '@/utils/toaster'
import { OUTCOME_TYPES } from '../utils/bot-json.constants'

class TemplateController extends PreviewController {

  addBubble(node, isInsideCondition, templateId, answerEndType) {
    checkIsObject(node, 'node')
    checkIsString(templateId, 'templateId')

    const newStateId = super.addBubble(node, isInsideCondition, templateId)
    if (newStateId !== STATE_ID.ROOT) {
      this.processTemplateRefs(node, isInsideCondition, newStateId, templateId, answerEndType)
    }

    return newStateId
  }

  addWaitForUserInput(node, isInsideCondition, templateId) {
    checkIsObject(node, 'node')
    checkIsString(templateId, 'templateId')

    const stateId = node.stateId
    const transitionStateId = super.getTransitionsStateId(stateId, isInsideCondition)
    const transitions = []
    if (isInsideCondition && head(node.children).node.type === NODE_TYPES.CONTINUE_CONDITION) {
      const continueConditionTransition = conversationModule.getFirstStateTransition(transitionStateId)
      transitions.push(continueConditionTransition)
    }
    const nextStateId = this.addStateToTemplate(transitionStateId, templateId, { input: INPUT_TYPES.USER_INPUT, expression: { true: {} } }, transitions)
    const newActionId = conversationModule.getStateActions(nextStateId)[0]
    chatBubbleModule.selectBotBubble(newActionId, nextStateId, SECTION_FOCUSES.TEXT)

    return { nextStateId, transitionStateId }
  }

  addSaveContextState(stateId, nextStateId, contextStateId, contextKey, templateId) {
    super.addSaveContextState(stateId, nextStateId, contextStateId, contextKey)

    const stateRefs = conversationModule.findAllTemplateRefsForState(this.getTransitionsStateId(stateId), templateId)
    const nextStateRefs = conversationModule.findAllTemplateRefsForState(nextStateId, templateId)
    for(const stateRefObj of stateRefs) {
      const stateRef = stateRefObj.state
      const stateRefId = stateRefObj.stateName
      const convType = stateRefObj.type
      const convId = stateRefObj.id
      const nextRefId = this._findMatchingRefId(stateRef.postbackSuffix, nextStateRefs)
      const templateRefTransitions = clone(conversationModule.getStateTemplateTransitions(stateRefId, convType, convId))
      const contextStateRefId = contextStateId + stateRef.postbackSuffix

      conversationModule.createTemplateRefState(convType, convId, contextStateRefId, templateId, contextStateId, stateRef.postbackSuffix, templateRefTransitions)
      conversationModule.replaceLastTemplateTransitionWithExpressionTrue(stateRefId, contextStateRefId, INPUT_TYPES.USER_INPUT, convType, convId)
      conversationModule.replaceLastTemplateTransitionWithExpressionTrue(contextStateRefId, nextRefId, INPUT_TYPES.NONE, convType, convId)
    }
  }

  addPostback(stateId, postbackKey, postbackValue, templateId, postbackExists) {
    checkIsString(stateId, 'stateId')
    checkIsString(postbackKey, 'postbackKey')
    checkIsString(postbackValue, 'postbackValue')
    checkIsString(templateId, 'templateId')

    if (!postbackExists && !postbackValue.startsWith('ref:')) {
      const postbackActionId = this.generateId()
      conversationModule.createTemplateState(postbackKey, postbackActionId)
    }
    conversationModule.addPostback(postbackKey, postbackValue)

    const stateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
    for(const stateRefObj of stateRefs) {
      const stateRef = stateRefObj.state
      const convType = stateRefObj.type
      const convId = stateRefObj.id
      const postbackSuffix = stateRef.postbackSuffix
      const newTemplateRefId = postbackKey + postbackSuffix
      const resolvedPostbackValue = postbackValue.startsWith('ref:') ? postbackValue : postbackValue + postbackSuffix

      if (!postbackValue.startsWith('ref:')) conversationModule.createTemplateRefState(convType, convId, newTemplateRefId, templateId, postbackKey, postbackSuffix, [], true)
      conversationModule.addPostback(newTemplateRefId, resolvedPostbackValue, convType, convId)
    }
  }

  addQuickReply(stateId, actionId, qrTitle, qrPayload, templateId) {
    checkIsString(templateId, 'templateId')
    const parentStateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
    this._checkStateRefTransitions(parentStateRefs)

    super.addQuickReply(stateId, actionId, qrTitle, qrPayload, templateId)
    for(const parentStateRefObj of parentStateRefs) {
      const parentStateRefId = parentStateRefObj.stateName
      const convType = parentStateRefObj.type
      const convId = parentStateRefObj.id

      conversationModule.addStateTemplateTransition(convType, convId, parentStateRefId, STATE_ID.REF_GLOBAL_IDLE, {
        input: INPUT_TYPES.QUICK_REPLY,
        expression: { equals: qrPayload }
      })
    }
  }

  updateQuickReplyRequestOutcome(stateId, transitionIndex, requestOutcome, templateId) {
    super.updateQuickReplyRequestOutcome(stateId, transitionIndex, requestOutcome, templateId)
    const parentStateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
    for(const parentStateRefObj of parentStateRefs) {
      const parentStateRefId = parentStateRefObj.stateName
      const convType = parentStateRefObj.type
      const convId = parentStateRefObj.id
      const templateTransitions = clone(conversationModule.getStateTemplateTransitions(parentStateRefId, convType, convId))
      if (requestOutcome) {
        templateTransitions[transitionIndex].requestOutcome = requestOutcome
      } else {
        delete templateTransitions[transitionIndex].requestOutcome
      }
      conversationModule.setStateTemplateTransitions(parentStateRefId, templateTransitions, convType, convId)
    }
  }

  addRemoteBubble(node, isInsideCondition, templateId, answerEndType = null) {
    checkIsObject(node, 'node')
    const remoteStateId = super.addRemoteBubble(node, isInsideCondition, templateId, answerEndType)
    this.processTemplateRefs(node, isInsideCondition, remoteStateId, templateId, answerEndType)
    return remoteStateId
  }

  addChatGPTBubble(node, isInsideCondition, templateId, answerEndType = null) {
    checkIsObject(node, 'node')
    const remoteStateId = super.addChatGPTBubble(node, isInsideCondition, templateId, answerEndType)
    this.processTemplateRefs(node, isInsideCondition, remoteStateId, templateId, answerEndType)
    return remoteStateId
  }

  processTemplateRefs(node, isInsideCondition, stateId, templateId, answerEndType) {
    if (node.type === NODE_TYPES.EMPTY && node.parent) {
      if (node.parent.transition.type === EDGE_TRANSITION_TYPES.POSTBACK) {
        this.addPostback(node.parent.node.stateId, node.parent.transition.postback, node.parent.transition.postback, templateId, true)
      } else if (node.parent.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY) {
        this.addStateToDanglingTemplateRefs(node.parent.node.stateId, templateId, stateId)
      } else if (node.parent.transition.type === EDGE_TRANSITION_TYPES.USER_INPUT ||
                 node.parent.transition.type === EDGE_TRANSITION_TYPES.NONE) {
        this.addStateToTemplateRefs(node.parent.node.stateId, templateId, stateId)
      }
    } else if (node.type === NODE_TYPES.CONDITION) {
      if (isInsideCondition) {
        this.addStateToConditionStateTemplateRefs(node.stateId, templateId, stateId)
      } else {
        this.addStateToTemplateRefs(node.conditionEndStateId, templateId, stateId, this.isContinueCondition(node, isInsideCondition, answerEndType))
      }
    } else if ([NODE_TYPES.REMOTE, NODE_TYPES.CHAT_GPT].includes(node.type) || conversationModule.isRemoteState(stateId)) {
      const remoteActionId = conversationModule.getStateActions(stateId)[0]
      const remoteAction = conversationModule.getAction(stateId, remoteActionId)
      const requestOutcome = [ACTION_TYPES.CHAT_GPT_GENERAL, ACTION_TYPES.CHAT_GPT_CUSTOM].includes(remoteAction.type) ?
        OUTCOME_TYPES.CUSTOMER_STARTED_TO_WRITE :
        null
      const resolvedStateId = node.parent?.node?.stateId || node.stateId
      this.addStateToTemplateRefs(resolvedStateId, templateId, stateId, this.isContinueCondition(node, isInsideCondition, answerEndType), requestOutcome, node)
    } else if ([NODE_TYPES.CONTINUE_ANSWER, NODE_TYPES.IDLE].includes(node.type)) {
      if (node.parent.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY) {
        const transitionIndex = node.parent.transition.index
        this.addStateToDanglingTemplateRefs(node.parent.node.stateId, templateId, stateId, transitionIndex)
      } else if (node.parent.transition.type === EDGE_TRANSITION_TYPES.POSTBACK) {
        this.addPostback(node.parent.node.stateId, node.parent.transition.postback, node.parent.transition.postback, templateId, true)
      }
    }
  }

  isContinueCondition(node, isInsideCondition, answerEndType) {
    return answerEndType === NODE_TYPES.CONTINUE_CONDITION ||
      (isInsideCondition && head(node.children).node.type === NODE_TYPES.CONTINUE_CONDITION)
  }

  addStateToTemplate(parentStateId, templateId, transitionCondition, transitions = []) {
    checkIsString(parentStateId, 'parentStateId')
    checkIsString(templateId, 'templateId')
    checkIsObject(transitionCondition, 'transitionCondition')

    const newStateId = this.generateId(templateId)
    const newActionId = this.generateId(newStateId)

    const parentStateRefs = conversationModule.findAllTemplateRefsForState(parentStateId, templateId)

    if(transitionCondition.input === INPUT_TYPES.QUICK_REPLY) {
      this._checkStateRefTransitions(parentStateRefs)
      if(isEmpty(conversationModule.getStateTransitions(parentStateId))) {
        conversationModule.addStateTransition(parentStateId, STATE_ID.REF_GLOBAL_FIND_INTENT, { input: INPUT_TYPES.USER_INPUT, expression: { true:{} } })
      }
    }
    conversationModule.createTemplateState(newStateId, newActionId, transitions)

    if(transitionCondition.input === INPUT_TYPES.USER_INPUT) {
      conversationModule.replaceLastTransitionWithExpressionTrue(parentStateId, newStateId, INPUT_TYPES.USER_INPUT)
    } else {
      conversationModule.addStateTransition(parentStateId, newStateId, transitionCondition)
    }

    this.addStateToTemplateRefs(parentStateId, templateId, newStateId, !isEmpty(transitions))

    return newStateId
  }

  addStateToConditionStateTemplateRefs(parentStateId, templateId, newStateId) {
    const conditionEndStateId = conversationModule.getConditionEndStateId(parentStateId)
    const parentStateRefs = conversationModule.findAllTemplateRefsForState(parentStateId, templateId)
    const caseIndex = chatBubbleModule.getConditionCaseSelected(parentStateId)
    for(const parentStateRefObj of parentStateRefs) {
      const parentStateRef = parentStateRefObj.state
      const parentStateRefId = parentStateRefObj.stateName
      const convType = parentStateRefObj.type
      const convId = parentStateRefObj.id

      const postbackSuffix = parentStateRef.postbackSuffix
      const newTemplateRefId = newStateId + postbackSuffix
      conversationModule.createTemplateRefState(convType, convId, newTemplateRefId, templateId, newStateId, postbackSuffix, [], false)

      const conditionEndStateRefId = conversationModule.getConditionEndStateRefId(conditionEndStateId, postbackSuffix, templateId)
      const transition = this._redirectConditionCaseTransition(
        conversationModule.getStateTemplateTransitions(parentStateRefId, convType, convId)[caseIndex],
        newTemplateRefId
      )
      conversationModule.updateStateTemplateTransition(parentStateRefId, transition, caseIndex, convType, convId)
      conversationModule.replaceLastTemplateTransitionWithExpressionTrue(newTemplateRefId, conditionEndStateRefId, INPUT_TYPES.NONE, convType, convId)
    }
  }

  addStateToDanglingTemplateRefs(parentStateId, templateId, newStateId, transitionIndex = -1) {
    const parentStateRefs = conversationModule.findAllTemplateRefsForState(parentStateId, templateId)
    for(const parentStateRefObj of parentStateRefs) {
      const parentStateRef = parentStateRefObj.state
      const convType = parentStateRefObj.type
      const convId = parentStateRefObj.id

      const postbackSuffix = parentStateRef.postbackSuffix
      const templateStateRefId = newStateId + postbackSuffix
      conversationModule.createTemplateRefState(convType, convId, templateStateRefId, templateId, newStateId, postbackSuffix, [], true)
      if (transitionIndex !== -1) {
        conversationModule.updateStateTemplateTransitionStateIdByIndex(parentStateRefObj.stateName, templateStateRefId, transitionIndex, convType, convId)
      }
    }
  }

  addStateToTemplateRefs(parentStateId, templateId, newStateId, isContinueCondition = false, requestOutcome = null, node = null) {
    const transitionIndex = [NODE_TYPES.CONTINUE_ANSWER, NODE_TYPES.IDLE].includes(node?.type) && node?.parent?.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY ?
      node.parent.transition.index :
      -1
    const isPostback = node?.parent?.transition?.type === EDGE_TRANSITION_TYPES.POSTBACK

    const parentStateRefs = conversationModule.findAllTemplateRefsForState(parentStateId, templateId)
    const transition = clone(find(propEq('state', newStateId), conversationModule.getStateTransitions(parentStateId)))
    for(const parentStateRefObj of parentStateRefs) {
      const parentStateRef = parentStateRefObj.state
      const parentStateRefId = parentStateRefObj.stateName
      const convType = parentStateRefObj.type
      const convId = parentStateRefObj.id

      const postbackSuffix = parentStateRef.postbackSuffix
      const templateTransitions = isContinueCondition ? parentStateRef.templateTransitions : []
      const newTemplateRefId = newStateId + postbackSuffix
      conversationModule.createTemplateRefState(convType, convId, newTemplateRefId, templateId, newStateId, postbackSuffix, templateTransitions, false, requestOutcome)

      if (transitionIndex !== -1) {
        conversationModule.updateStateTemplateTransitionStateIdByIndex(parentStateRefId, newTemplateRefId, transitionIndex, convType, convId)
        continue
      }

      if (isPostback) {
        conversationModule.addPostback(node.parent.transition.postback + postbackSuffix, newTemplateRefId, convType, convId)
      }

      if (newStateId !== STATE_ID.ROOT) {
        if(isEmpty(parentStateRef.templateTransitions) && transition?.condition.input === INPUT_TYPES.QUICK_REPLY) {
          conversationModule.addStateTemplateTransition(convType, convId, parentStateRefId, STATE_ID.REF_GLOBAL_FIND_INTENT, { input: INPUT_TYPES.USER_INPUT, expression: { true:{} } })
        }
        if (transition) conversationModule.addStateTemplateTransition(convType, convId, parentStateRefId, newTemplateRefId, transition.condition)
      }

      const parentStateRefTransitions = conversationModule.getStateTransitions(parentStateRefId, convType, convId)
      if(parentStateRefTransitions.length === 1 && !isPostback) {
        conversationModule.setStateTransitions(parentStateRefId, [], convType, convId)
        conversationModule.setStateTransitions(newTemplateRefId, parentStateRefTransitions, convType, convId)
        if (parentStateRefId === STATE_ID.ROOT && newStateId === STATE_ID.ROOT) {
          conversationModule.removeState(parentStateRefId, convType, convId)
          conversationModule.renameState(newTemplateRefId, parentStateRefId, convType, convId)
        }
      } else {
        conversationModule.removeLastTemplateTransition(parentStateRefId, convType, convId)
        if(!isContinueCondition) conversationModule.addStateTransition(newTemplateRefId, STATE_ID.REF_GLOBAL_FIND_INTENT, { input: INPUT_TYPES.USER_INPUT, expression: { true:{} } }, null, convType, convId)
      }
    }
  }

  addConditionSection(node, templateId) {
    const stateExists = conversationModule.stateExists(node.stateId)
    const {
      conditionStateId,
      conditionEndStateId
    } = this.addConditionSectionWithTransitions(node, [])

    const nodeIsPostback = stateTreeUtils.nodeIsPostback(node)
    const nodeIsQuickReply = stateTreeUtils.nodeIsQuickReply(node)
    const isWaitForInput = [NODE_TYPES.SAVE_CONTEXT, NODE_TYPES.VALIDATION].includes(node.parent?.node.type)
    if (node.type === NODE_TYPES.EMPTY || nodeIsQuickReply || nodeIsPostback) {
      const stateId = node.parent?.node.stateId || node.stateId
      const transitionIndex = nodeIsQuickReply ? node.parent.transition.index : -1
      const parentStateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
      for(const parentStateRefObj of parentStateRefs) {
        const parentStateRef = parentStateRefObj.state
        const convType = parentStateRefObj.type
        const convId = parentStateRefObj.id
        const postbackSuffix = parentStateRef.postbackSuffix
        const conditionEndStateRefId = conditionEndStateId + postbackSuffix
        conversationModule.createTemplateRefState(convType, convId, conditionEndStateRefId, templateId, conditionEndStateId, postbackSuffix, [], true)
        const templateTransitions = [{
          state: conditionEndStateRefId,
          condition: {
            input: INPUT_TYPES.NONE,
            expression: { true: {} }
          }
        }]

        if (stateExists && !nodeIsQuickReply && !nodeIsPostback) {
          // rootStateRef already exists, need to update transitions + templateTransitions
          const conditionStateRefId = parentStateRefObj.stateName
          conversationModule.setStateTemplateTransitions(conditionStateRefId, templateTransitions, convType, convId)
          const transitions = conversationModule.getStateTransitions(conditionStateRefId, convType, convId)
          const resolvedTransitions = isEmpty(transitions) ? [conversationModule.createFindIntentTransition()] : transitions
          conversationModule.setStateTransitions(conditionEndStateRefId, resolvedTransitions, convType, convId)
          conversationModule.setStateTransitions(conditionStateRefId, [], convType, convId)
          continue
        }

        const conditionStateRefId = conditionStateId + postbackSuffix
        conversationModule.createTemplateRefState(convType, convId, conditionStateRefId, templateId, conditionStateId, postbackSuffix, templateTransitions)

        if (nodeIsPostback) {
          conversationModule.addPostback(node.parent.transition.postback + postbackSuffix, conditionStateRefId, convType, convId)
        }

        if (nodeIsQuickReply || isWaitForInput) {
          if (isEmpty(conversationModule.getStateTemplateTransitions(parentStateRefObj.stateName, convType, convId))) {
            conversationModule.setStateTemplateTransitions(parentStateRefObj.stateName, [{
              state: conditionStateRefId,
              condition: {
                input: INPUT_TYPES.NONE,
                expression: { true: {} }
              }
            }], convType, convId)
            conversationModule.setStateTransitions(parentStateRefObj.stateName, [], convType, convId)
          } else if (nodeIsQuickReply) {
            conversationModule.updateStateTemplateTransitionStateIdByIndex(parentStateRefObj.stateName, conditionStateRefId, transitionIndex, convType, convId)
          }
        }
      }
      return { conditionStateId, conditionEndStateId }
    }

    const stateId = this.getTransitionsStateId(node.stateId)
    const stateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
    for(const stateRefObj of stateRefs) {
      const { postbackSuffix, templateTransitions, transitions } = stateRefObj.state
      const stateRefId = stateRefObj.stateName
      const convType = stateRefObj.type
      const convId = stateRefObj.id
      const conditionEndStateRefId = conditionEndStateId + postbackSuffix
      const addDefaultTransition = templateTransitions.length && templateTransitions[0].state.startsWith('ref:')
      const resolvedTemplateTransitions = addDefaultTransition ? [] : templateTransitions
      conversationModule.createTemplateRefState(convType, convId, conditionEndStateRefId, templateId, conditionEndStateId, postbackSuffix, resolvedTemplateTransitions, addDefaultTransition)
      if (!isEmpty(transitions)) {
        conversationModule.setStateTransitions(conditionEndStateRefId, transitions, convType, convId)
      }

      const conditionStateRefId = conditionStateId + postbackSuffix
      conversationModule.createTemplateRefState(convType, convId, conditionStateRefId, templateId, conditionStateId, postbackSuffix, [{
        state: conditionEndStateRefId,
        condition: {
          input: INPUT_TYPES.NONE,
          expression: { true:{} }
        }
      }])
      conversationModule.removeLastTransition(stateRefId, convType, convId)
      conversationModule.replaceLastTemplateTransitionWithExpressionTrue(stateRefId, conditionStateRefId, INPUT_TYPES.NONE, convType, convId)
    }

    return { conditionStateId, conditionEndStateId }
  }

  addCaseToCondition(conditionStateId, condition, templateId) {
    const caseStateId = super.addCaseToCondition(conditionStateId, condition, templateId)
    const conditionEndStateId = conversationModule.getConditionEndStateId(conditionStateId)
    const conditionStateRefs = conversationModule.findAllTemplateRefsForState(conditionStateId, templateId)
    for(const conditionStateRefObj of conditionStateRefs) {
      const conditionStateRefId = conditionStateRefObj.stateName
      const postbackSuffix = conditionStateRefObj.state.postbackSuffix
      const convType = conditionStateRefObj.type
      const convId = conditionStateRefObj.id
      const conditionEndStateRefId = conversationModule.getConditionEndStateRefId(conditionEndStateId, postbackSuffix, templateId)
      const caseStateRefId = caseStateId + postbackSuffix

      conversationModule.createTemplateRefState(convType, convId, caseStateRefId, templateId, caseStateId, postbackSuffix, [{
        state: conditionEndStateRefId,
        condition: {
          input: INPUT_TYPES.NONE,
          expression: { true: {} }
        }
      }])

      conversationModule.addStateTemplateTransition(convType, convId, conditionStateRefId, caseStateRefId, condition)
    }

    return caseStateId
  }

  updateCase(conditionStateId, transition, transitionIndex, templateId) {
    super.updateCase(conditionStateId, transition, transitionIndex, templateId)

    const conditionStateRefs = conversationModule.findAllTemplateRefsForState(conditionStateId, templateId)
    for(const conditionStateRefObj of conditionStateRefs) {
      const conditionStateRefId = conditionStateRefObj.stateName
      const convType = conditionStateRefObj.type
      const convId = conditionStateRefObj.id
      const templateRefTransitions = clone(conversationModule.getStateTemplateTransitions(conditionStateRefId, convType, convId))
      const templateRefTransition = templateRefTransitions[transitionIndex]
      templateRefTransition.condition = transition.condition

      conversationModule.updateStateTemplateTransition(conditionStateRefId, templateRefTransition, transitionIndex, convType, convId)
    }
  }

  reorderTransitions(stateId, fromIndex, toIndex, templateId) {
    super.reorderTransitions(stateId, fromIndex, toIndex, templateId)

    const stateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
    for(const stateRefObj of stateRefs) {
      const stateRefId = stateRefObj.stateName
      const convType = stateRefObj.type
      const convId = stateRefObj.id

      const templateRefTransitions = conversationModule.getStateTemplateTransitions(stateRefId, convType, convId)
      const reorderedTemplateRefTransitions = move(fromIndex, toIndex, templateRefTransitions)
      conversationModule.setStateTemplateTransitions(stateRefId, reorderedTemplateRefTransitions, convType, convId)
    }
  }

  removeCaseFromCondition(conditionStateId, caseIndex, templateId) {
    super.removeCaseFromCondition(conditionStateId, caseIndex, templateId)

    const conditionStateRefs = conversationModule.findAllTemplateRefsForState(conditionStateId, templateId)
    for(const conditionStateRefObj of conditionStateRefs) {
      const conditionStateRefId = conditionStateRefObj.stateName
      const convType = conditionStateRefObj.type
      const convId = conditionStateRefObj.id
      conversationModule.removeTemplateTransitionIndex(conditionStateRefId, caseIndex, convType, convId)
    }
  }

  removeWaitForInput(stateTree, bubbleSelected, templateId) {
    super.removeWaitForInput(stateTree, bubbleSelected, templateId)

    const { nextStateId, stateId, contextStateId } = bubbleSelected
    if (!conversationModule.getStateTransitions(nextStateId)) {
      conversationModule.setStateTransitions(stateId, [])
    }

    const stateRefs = conversationModule.findAllTemplateRefsForState(this.getTransitionsStateId(stateId), templateId)
    const nextStateRefs = conversationModule.findAllTemplateRefsForState(nextStateId, templateId)
    const contextStateRefs = conversationModule.findAllTemplateRefsForState(contextStateId, templateId)
    for(const stateRefObj of stateRefs) {
      const stateRef = stateRefObj.state
      const stateRefId = stateRefObj.stateName
      const convType = stateRefObj.type
      const convId = stateRefObj.id
      const postbackSuffix = stateRef.postbackSuffix
      const nextRefId = this._findMatchingRefId(postbackSuffix, nextStateRefs)
      const contextStateRefId = this._findMatchingRefId(postbackSuffix, contextStateRefs)
      const contextStateRefTransitions = contextStateRefId ? conversationModule.getStateTransitions(contextStateRefId, convType, convId) : null
      if (contextStateRefId) {
        conversationModule.removeState(contextStateRefId, convType, convId)
      }
      // HACK: correct processing when called from previewController.removeNode
      if (bubbleSelected.replaceWithNextRefState) {
        const nextRefTemplateTransitions = conversationModule.getStateTemplateTransitions(nextRefId, convType, convId)
        const nextRefTransitions = conversationModule.getStateTransitions(nextRefId, convType, convId)
        conversationModule.setStateTemplateTransitions(stateRefId, nextRefTemplateTransitions, convType, convId)
        conversationModule.setStateTransitions(stateRefId, nextRefTransitions, convType, convId)
        conversationModule.removeState(nextRefId, convType, convId)
        continue
      }
      if (!nextRefId && contextStateRefTransitions) {
        conversationModule.setStateTemplateTransitions(stateRefId, [], convType, convId)
        conversationModule.setStateTransitions(stateRefId, contextStateRefTransitions, convType, convId)
        continue
      }
      conversationModule.replaceLastTemplateTransitionWithExpressionTrue(stateRefId, nextRefId, INPUT_TYPES.NONE, convType, convId)
    }
  }

  removeSaveContextState(stateId, nextStateId, contextStateId, templateId) {
    super.removeSaveContextState(stateId, nextStateId, contextStateId, templateId)

    const stateRefs = conversationModule.findAllTemplateRefsForState(this.getTransitionsStateId(stateId), templateId)
    const nextStateRefs = conversationModule.findAllTemplateRefsForState(nextStateId, templateId)
    const contextStateRefs = conversationModule.findAllTemplateRefsForState(contextStateId, templateId)
    for(const stateRefObj of stateRefs) {
      const stateRef = stateRefObj.state
      const stateRefId = stateRefObj.stateName
      const convType = stateRefObj.type
      const convId = stateRefObj.id
      const postbackSuffix = stateRef.postbackSuffix
      const nextRefId = this._findMatchingRefId(postbackSuffix, nextStateRefs)
      const contextStateRefId = this._findMatchingRefId(postbackSuffix, contextStateRefs)
      conversationModule.replaceLastTemplateTransitionWithExpressionTrue(stateRefId, nextRefId, INPUT_TYPES.USER_INPUT, convType, convId)
      conversationModule.removeState(contextStateRefId, convType, convId)
    }
  }

  updateAnswerEnd(stateId, type, bubbleSelected, templateId, answerId, transitionIndex = -1) {
    const isCondition = bubbleSelected.isCondition
    const conditionEndStateId = bubbleSelected.conditionEndStateId
    const actualStateId = this.getStateIdForAnswerEnd(stateId, isCondition, templateId)

    const stateRefs = conversationModule.findAllTemplateRefsForState(actualStateId, templateId)
    if (type === ANSWER_END.FIND_INTENT) {
      this._checkStateRefTransitions(stateRefs)

      this.replaceTransition(actualStateId, STATE_ID.REF_GLOBAL_FIND_INTENT)
      for(const stateRefObj of stateRefs) {
        const stateRefId = stateRefObj.stateName
        const convType = stateRefObj.type
        const convId = stateRefObj.id
        this.replaceTemplateTransition(stateRefId, STATE_ID.REF_GLOBAL_FIND_INTENT, convType, convId, isCondition)
        conversationModule.setStateTransitions(stateRefId, [], convType, convId)
      }
    }

    if (type === ANSWER_END.CONTINUE_ANSWER) {
      this._checkStateRefTransitions(stateRefs)

      const refAnswerId = 'ref:answer:' + answerId
      if (bubbleSelected.isPostback && !conversationModule.stateExists(bubbleSelected.stateId)) {
        conversationModule.addPostback(bubbleSelected.stateId, refAnswerId)
      } else if (transitionIndex !== -1) {
        conversationModule.updateStateTransitionStateIdByIndex(actualStateId, refAnswerId, transitionIndex)
      } else {
        this.replaceTransition(actualStateId, refAnswerId)
      }

      for(const stateRefObj of stateRefs) {
        const stateRefId = stateRefObj.stateName
        const convType = stateRefObj.type
        const convId = stateRefObj.id
        if (bubbleSelected.isPostback && !conversationModule.stateExists(bubbleSelected.stateId)) {
          conversationModule.addPostback(bubbleSelected.stateId + stateRefObj.state.postbackSuffix, refAnswerId, convType, convId)
        } else if (transitionIndex !== -1) {
          conversationModule.updateStateTemplateTransitionStateIdByIndex(stateRefId, refAnswerId, transitionIndex, convType, convId)
        } else {
          this.replaceTemplateTransition(stateRefId, refAnswerId, convType, convId, isCondition)
        }
        conversationModule.setStateTransitions(stateRefId, [], convType, convId)
      }
    }

    if (type === ANSWER_END.IDLE) {
      this._checkStateRefTransitions(stateRefs)

      if (bubbleSelected.isPostback && !conversationModule.stateExists(bubbleSelected.stateId)) {
        conversationModule.addPostback(bubbleSelected.stateId, STATE_ID.REF_GLOBAL_IDLE)
      } else if (transitionIndex !== -1) {
        conversationModule.updateStateTransitionStateIdByIndex(actualStateId, STATE_ID.REF_GLOBAL_IDLE, transitionIndex)
      } else {
        this.replaceTransition(actualStateId, STATE_ID.REF_GLOBAL_IDLE)
      }

      for(const stateRefObj of stateRefs) {
        const stateRefId = stateRefObj.stateName
        const convType = stateRefObj.type
        const convId = stateRefObj.id
        if (bubbleSelected.isPostback && !conversationModule.stateExists(bubbleSelected.stateId)) {
          conversationModule.addPostback(bubbleSelected.stateId + stateRefObj.state.postbackSuffix, STATE_ID.REF_GLOBAL_IDLE, convType, convId)
        } else if (transitionIndex !== -1) {
          conversationModule.updateStateTemplateTransitionStateIdByIndex(stateRefId, STATE_ID.REF_GLOBAL_IDLE, transitionIndex, convType, convId)
        } else {
          this.replaceTemplateTransition(stateRefId, STATE_ID.REF_GLOBAL_IDLE, convType, convId, isCondition)
        }

        conversationModule.setStateTransitions(stateRefId, [], convType, convId)
      }
    }

    if (type === ANSWER_END.WAIT_INPUT_THEN_IDLE) {
      this._checkStateRefTransitions(stateRefs)
      this.replaceTransitionAndInputType(actualStateId, STATE_ID.REF_GLOBAL_IDLE, INPUT_TYPES.USER_INPUT)

      for(const stateRefObj of stateRefs) {
        const stateRefId = stateRefObj.stateName
        const convType = stateRefObj.type
        const convId = stateRefObj.id
        this.replaceTemplateTransitionAndInputType(stateRefId, STATE_ID.REF_GLOBAL_IDLE, convType, convId, INPUT_TYPES.USER_INPUT, isCondition)

        conversationModule.setStateTransitions(stateRefId, [], convType, convId)
      }
    }

    if (type === ANSWER_END.CONTINUE_CONDITION) {
      this.replaceTransition(actualStateId, conditionEndStateId)
      for(const stateRefObj of stateRefs) {
        const stateRefId = stateRefObj.stateName
        const convType = stateRefObj.type
        const convId = stateRefObj.id
        const conditionEndStateRefId = conversationModule.getConditionEndStateRefId(conditionEndStateId, stateRefObj.state.postbackSuffix, templateId)
        this.replaceTemplateTransition(stateRefId, conditionEndStateRefId, convType, convId, isCondition)
      }
    }

    if(type === ANSWER_END.CONTINUE_TEMPLATE) {
      conversationModule.removeLastTransition(actualStateId)
      for(const stateRefObj of stateRefs) {
        const stateRefId = stateRefObj.stateName
        const convType = stateRefObj.type
        const convId = stateRefObj.id
        conversationModule.removeLastTemplateTransition(stateRefId, convType, convId)
        if(isEmpty(stateRefObj.state.transitions)) {
          conversationModule.addStateTransition(stateRefId, STATE_ID.REF_GLOBAL_FIND_INTENT, {
            input: 'userInput',
            expression: {
              true: {}
            }
          }, null, convType, convId)
        }
      }
    }
  }

  removeNode(node, templateId) {
    // save refs in case we will need them later
    const removedTemplateStateId = node.stateId
    const removedTemplateStateRefs = conversationModule.findAllTemplateRefsForState(node.stateId, templateId)

    const isChildNodeContinueTemplate = node?.children?.[0]?.node.type === NODE_TYPES.CONTINUE_TEMPLATE
    if ([EDGE_TRANSITION_TYPES.QUICK_REPLY, EDGE_TRANSITION_TYPES.POSTBACK].includes(node.parent?.transition?.type) && isChildNodeContinueTemplate) {
      const stateId = node.type === NODE_TYPES.CONDITION ? node.conditionEndStateId : node.stateId
      const templateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
      for(const templateRefObj of templateRefs) {
        const transitionStateId = head(templateRefObj.state.transitions)?.state
        if (transitionStateId && ![STATE_ID.REF_GLOBAL_FIND_INTENT, STATE_ID.IDLE].includes(transitionStateId)) {
          setTimeout(() => {
            // HACK: delay toaster so that unit test can process thrown Err (else Vue.$toast will throw an Err first)
            toaster.error('Cannot delete because there are attached states in Answers. Workaround:<br/>1. Add a new state.<br/>2. Select + Delete what you wanted to remove')
          }, 100)
          throw new Error('Cannot delete because there are attached states in Answers')
        }
      }
    }

    super.removeNode(node, templateId)
    // special handling when we're removing the first state of a template
    if (isNil(node.parent) && node.children) {
      const nextState = this._findNextState(node)

      // in order to not break anything, we have to rename the next state (and refs)
      // 1. rename the template state
      // 2. rename each template ref of the next state
      // 3. update each template ref's templateState parameter so it points to the correct (renamed) state
      if (nextState) {

        // the save context state is being removed in removeBubble() but we still have to do the renaming here
        // and we have to use next next state because the stateTree is not updated so the save context node is still there

        // TODO maybe just rename to root
        if(nextState.node.type === NODE_TYPES.SAVE_CONTEXT) {
          const nextNextStateId = nextState.node.children[0].node.stateId
          const nextNextStateRefs = conversationModule.findAllTemplateRefsForState(nextNextStateId, templateId)
          for(const nextNextRefObj of nextNextStateRefs) {
            const nextNextRefId = nextNextRefObj.stateName
            const nextNextRef = nextNextRefObj.state
            const convType = nextNextRefObj.type
            const convId = nextNextRefObj.id
            const stateRefId = this._findMatchingRefId(nextNextRef.postbackSuffix, removedTemplateStateRefs)
            conversationModule.updateStateTemplateState(convType, convId, nextNextRefId, removedTemplateStateId)
            conversationModule.renameState(nextNextRefId, stateRefId, convType, convId)
          }
          conversationModule.renameState(nextNextStateId, removedTemplateStateId)
        } else {
          const nextStateRefs = conversationModule.findAllTemplateRefsForState(nextState.node.stateId, templateId)
          for(const nextRefObj of nextStateRefs) {
            const nextRefId = nextRefObj.stateName
            const nextRef = nextRefObj.state
            const convType = nextRefObj.type
            const convId = nextRefObj.id
            const stateRefId = this._findMatchingRefId(nextRef.postbackSuffix, removedTemplateStateRefs)
            conversationModule.updateStateTemplateState(convType, convId, nextRefId, removedTemplateStateId)
            conversationModule.renameState(nextRefId, stateRefId, convType, convId)
          }
          conversationModule.renameState(nextState.node.stateId, removedTemplateStateId)
        }
      }
    }
  }

  removeStateFromAnswer(node, templateId) {
    const parentTransitionType = node.parent?.transition?.type
    const parentNode = node?.parent?.node
    const removedStateRefs = conversationModule.findAllTemplateRefsForState(node.stateId, templateId)

    if(parentNode) {
      switch(parentTransitionType) {
        case EDGE_TRANSITION_TYPES.USER_INPUT:
        case EDGE_TRANSITION_TYPES.NONE:
        case EDGE_TRANSITION_TYPES.CONDITION: {
          const inCondition = parentTransitionType === EDGE_TRANSITION_TYPES.CONDITION
          const parentStateId = inCondition ? parentNode.stateId : this.getTransitionsStateId(parentNode.stateId)
          const parentStateRefs = conversationModule.findAllTemplateRefsForState(parentStateId, templateId)
          const isLastBubble = node.stateId === STATE_ID.ROOT && !this._findNextState(node)

          for(const parentStateRefObj of parentStateRefs) {
            const parentStateRef = parentStateRefObj.state
            const parentStateRefId = parentStateRefObj.stateName
            const convType = parentStateRefObj.type
            const convId = parentStateRefObj.id
            const postbackSuffix = parentStateRef.postbackSuffix
            const removedStateRefId = this._findMatchingRefId(postbackSuffix, removedStateRefs)

            const transitions = this.setPrevTransitionToNextState(removedStateRefId, parentStateRefId, inCondition, convType, convId)
            conversationModule.setStateTransitions(parentStateRefId, transitions, convType, convId)
            const templateTransitions = isLastBubble ? [] : this.setPrevTemplateTransitionToNextState(removedStateRefId, parentStateRefId, inCondition, convType, convId)
            conversationModule.setStateTemplateTransitions(parentStateRefId, templateTransitions, convType, convId)
          }
          break
        }
        case EDGE_TRANSITION_TYPES.QUICK_REPLY:
          const templateTransitionStateIds = {}
          const stateId = node.type === NODE_TYPES.CONDITION ? node.conditionEndStateId : node.stateId
          const templateRefs = conversationModule.findAllTemplateRefsForState(stateId, templateId)
          for(const templateRefObj of templateRefs) {
            const templateTransitionStateId = head(templateRefObj.state.templateTransitions)?.state
            if (templateTransitionStateId) templateTransitionStateIds[templateRefObj.id] = templateTransitionStateId
          }

          const transitionIndex = parentNode.children.findIndex(c => c.node.stateId === node.stateId)
          const parentStateRefs = conversationModule.findAllTemplateRefsForState(parentNode.stateId, templateId)
          for(const parentStateRefObj of parentStateRefs) {
            const parentStateRefId = parentStateRefObj.stateName
            const convType = parentStateRefObj.type
            const convId = parentStateRefObj.id

            const templateTransition = clone(parentStateRefObj.state.templateTransitions[transitionIndex])
            if (templateTransition) {
              templateTransition.state = templateTransitionStateIds[convId] || STATE_ID.REF_GLOBAL_IDLE
              conversationModule.updateStateTemplateTransition(parentStateRefId, templateTransition, transitionIndex, convType, convId)
            }
          }
          break
        case EDGE_TRANSITION_TYPES.POSTBACK:
          const postbackExists = conversationModule.postbackExists(node.parent.transition.postback)
          if (postbackExists) {
            const nextStateId = last(node.children).node.stateId
            const resolvedStateId = nextStateId ? nextStateId : node.parent.node.stateId
            const stateTemplateRefs = conversationModule.findAllTemplateRefsForState(resolvedStateId, templateId)
            for(const stateTemplateRefObj of stateTemplateRefs) {
              const { type, id, state } = stateTemplateRefObj
              // updating postback value (postback stateId) when postback state is removed but postback still exists
              const refAnswerId = last(node.children).node.answerId ? 'ref:answer:' + last(node.children).node.answerId : null
              const postbackValue = nextStateId ? stateTemplateRefObj.stateName : refAnswerId || STATE_ID.REF_GLOBAL_IDLE
              conversationModule.addPostback(node.parent.transition.postback + state.postbackSuffix, postbackValue, type, id)
            }
          }
          break
        default:
          throw new Error('unknown transition type')
      }
    }

    const nextState = this._findNextState(node)
    // Checking if the final bubble of a template is being removed. If so, we don't want to remove the templateRefState.
    if (node.stateId !== STATE_ID.ROOT || nextState) {
      for(const removedStateRefObj of removedStateRefs) {
        conversationModule.removeState(removedStateRefObj.stateName, removedStateRefObj.type, removedStateRefObj.id)
      }
    }
    if(node.type === NODE_TYPES.CONDITION) {
      for(const conditionRefObj of removedStateRefs) {
        const conditionStateRef = conditionRefObj.state
        const convType = conditionRefObj.type
        const convId = conditionRefObj.id
        const postbackSuffix = conditionStateRef.postbackSuffix
        const conditionEndStateRefId = conversationModule.getConditionEndStateRefId(node.conditionEndStateId, postbackSuffix, templateId)
        if(node.stateId === STATE_ID.ROOT && !nextState) {
          // rootStateRef will not be deleted, need to update transitions + clear templateTransitions
          const condStateRefId = conditionRefObj.stateName
          const transitions = this.setPrevTransitionToNextState(conditionEndStateRefId, condStateRefId, true, convType, convId)
          conversationModule.setStateTransitions(condStateRefId, transitions, convType, convId)
          conversationModule.setStateTemplateTransitions(condStateRefId, [], convType, convId)
        }
        conversationModule.removeState(conditionEndStateRefId, convType, convId)
      }
    }

    // sets the correct transitions for the template parent state and deletes the condition state and condition end state from the template
    super.removeStateFromAnswer(node, templateId)
  }

  removePostbackNode(node, postbackKey, postbackParentNode, templateId) {
    const resolvedNode = [NODE_TYPES.EMPTY, NODE_TYPES.CONTINUE_ANSWER, NODE_TYPES.IDLE].includes(node.type) ? postbackParentNode : node
    const templateRefs = conversationModule.findAllTemplateRefsForState(resolvedNode.stateId, templateId)
    for(const templateRefObj of templateRefs) {
      conversationModule.removePostback(postbackKey + templateRefObj.state.postbackSuffix, templateRefObj.type, templateRefObj.id)
    }

    super.removePostbackNode(node, postbackKey, postbackParentNode, templateId)
  }

  removeQuickReplyNode(node, qrParentStateId, templateId, qrStateId = null) {
    const qrParentStateRefs = conversationModule.findAllTemplateRefsForState(qrParentStateId, templateId)
    const qrStateTransitions = conversationModule.getStateTransitions(qrParentStateId)
    const transitionStateId = qrStateId || this._getQuickReplyTransitionStateId(node)
    const qrIndex = qrStateTransitions.findIndex(({ state }) => state === transitionStateId)
    for(const parentStateRefObj of qrParentStateRefs) {
      const parentStateRefId = parentStateRefObj.stateName
      const parentStateRef = parentStateRefObj.state
      const convType = parentStateRefObj.type
      const convId = parentStateRefObj.id

      const templateTransitions = parentStateRef.templateTransitions.filter((templateTransition, index) => index !== qrIndex)
      conversationModule.setStateTemplateTransitions(parentStateRefId, templateTransitions, convType, convId)
    }

    super.removeQuickReplyNode(node, qrParentStateId, templateId, qrStateId)
  }

  removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId = null) {
    this._removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId)
  }

  removeQuickReplies(qrStateIds, qrParentStateId, qrParentStateActionId, templateId = null) {
    qrStateIds.forEach(qrStateId => this.removeQuickReply(qrStateId, qrParentStateId, qrParentStateActionId, templateId))
  }

  /**
   * checks if any of the state refs contain a transition that would prevent defining
   * a template transition (which is transition to any other state than findIntent)
   */
  _checkStateRefTransitions(stateRefs) {
    for(const stateRef of stateRefs) {
      if(find(propSatisfies(s => s !== STATE_ID.REF_GLOBAL_FIND_INTENT, 'state'), stateRef.state.transitions)) {
        throw new Error(`transition change error in state: ${stateRef.stateName}, ${stateRef.type}: ${stateRef.id}`)
      }
    }
  }

  _findMatchingRefId(postbackSuffix, refsList) {
    return find(pathEq(['state', 'postbackSuffix'], postbackSuffix), refsList)?.stateName
  }

  _findNextState(node) {
    return node.children.find((childEdge) => {
      return ![EDGE_TRANSITION_TYPES.POSTBACK, EDGE_TRANSITION_TYPES.QUICK_REPLY].includes(childEdge.transition.type) &&
        [NODE_TYPES.OUTPUT, NODE_TYPES.CONDITION, NODE_TYPES.REMOTE, NODE_TYPES.CHAT_GPT, NODE_TYPES.SAVE_CONTEXT].includes(childEdge.node.type)
    })
  }

  setPrevTemplateTransitionToNextState(stateId, parentStateId, inCondition, conversationType, conversationId) {
    const parentTransitionStateId = this.getTransitionsStateId(parentStateId, false, conversationType, conversationId)
    const parentTransitions = conversationModule.getStateTemplateTransitions(inCondition ? parentStateId : parentTransitionStateId, conversationType, conversationId)
    const stateTransitionStateId = this.getTransitionsStateId(stateId, false, conversationType, conversationId)
    const stateTransitions = conversationModule.getStateTemplateTransitions(stateTransitionStateId, conversationType, conversationId)
    const transition = stateTransitions.find(t => t.condition.input !== INPUT_TYPES.QUICK_REPLY)

    return this.moveTransitions(parentTransitions, transition, stateId, inCondition)
  }

  getStateIdForAnswerEnd(answerEndStateId, isCondition, templateId) {
    if(!isCondition && conversationModule.isTemplateRefState(answerEndStateId)) {
      const templateState = conversationModule.getTemplateStateForTemplateRef(answerEndStateId)
      const templateId = templateState.fromTemplate.replace('ref:template:', '')
      if(conversationModule.isConditionState(templateState, CONVERSATION_TYPES.TEMPLATES, templateId)) {
        return conversationModule.getConditionEndStateRefId(templateState.conditionEndState, conversationModule.getStateFromRootJson(answerEndStateId).postbackSuffix, templateId)
      }
    }
    return super.getStateIdForAnswerEnd(answerEndStateId, isCondition, templateId)
  }

  replaceTemplateTransition(stateId, templateTransitionStateId, conversationType, conversationId, isCondition = false) {
    const input = templateTransitionStateId === STATE_ID.REF_GLOBAL_FIND_INTENT ? INPUT_TYPES.USER_INPUT : INPUT_TYPES.NONE
    this.replaceTemplateTransitionAndInputType(stateId, templateTransitionStateId, conversationType, conversationId, input, isCondition)
  }

  replaceTemplateTransitionAndInputType(stateId, templateTransitionStateId, conversationType, conversationId, input, isCondition = false) {
    const endCondition = { input, expression: { true: {} } }
    if (isCondition) {
      const templateTransitionIndex = chatBubbleModule.getConditionCaseSelected(stateId)
      const templateTransition = conversationModule.getStateTemplateTransitions(stateId, conversationType, conversationId)[templateTransitionIndex]
      const expressionType = head(Object.keys(templateTransition.condition.expression))
      const condition = [EXPRESSION_TYPES.ENUM, EXPRESSION_TYPES.OPERATOR_AVAILABLE, EXPRESSION_TYPES.TIME_BETWEEN].includes(expressionType) ?
        templateTransition.condition :
        endCondition
      const updatedTemplateTransition = { condition, state: templateTransitionStateId }

      conversationModule.updateStateTemplateTransition(stateId, updatedTemplateTransition, templateTransitionIndex, conversationType, conversationId)
      return
    }

    conversationModule.replaceLastTemplateTransition(stateId, templateTransitionStateId, endCondition, conversationType, conversationId)
  }

}

export default new TemplateController()
